import React, { useState, useEffect, useRef } from 'react';
import Scanner from './Scanners/Scanner';
import TSDScanner from './Scanners/TSDScanner';
import api from '../../../../services/api';
import styles from './ContainerScannerModal.module.css';
import beepFile from './beep.mp3';

export default function ContainerScannerModal({
  container,
  containerId,
  isOpen,
  onClose,
  onScanSuccess,
}) {
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [selectedRow, setSelectedRow] = useState('auto');
  const [scannerType, setScannerType] = useState('tsd');

  const beepAudio = useRef(null);
  const inFlight = useRef(false);
  const cooldowns = useRef(new Map());

  useEffect(() => {
    beepAudio.current = new Audio(beepFile);
  }, []);

  // восстанавливаем выбранный тип сканера
  useEffect(() => {
    const saved = localStorage.getItem('scannerType');
    if (saved) setScannerType(saved);
  }, []);

  // загружаем данные контейнера при открытии
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setLastScan(null);

    const fetchInfo = async () => {
      setLoadingInfo(true);
      try {
        const data = await api.get(`/containers/${containerId}`);
        setInfo(data);
      } finally {
        setLoadingInfo(false);
      }
    };
    fetchInfo();
  }, [isOpen, containerId]);

  // Escape → закрыть
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && isOpen && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const humanType = info?.type === 'pallet' ? 'Паллет' : 'Поддон';
  const displayedCount = lastScan?.currentCount ?? info?.currentCount;
  const layoutRows = info?.layout?.rows || container?.layout?.rows || [];
  const marks = info?.marks || info?.marksInContainer || [];

  const countsByRow = layoutRows.map((cap, i) =>
    marks.filter((m) => m.row_index === i).length
  );

  const findFirstAvailableRow = () => {
    for (let i = 0; i < layoutRows.length; i++) {
      if (countsByRow[i] < layoutRows[i]) return i;
    }
    return null;
  };

  const handleMarkScanned = async (rawCode) => {
    const now = Date.now();
    if (inFlight.current) return;

    const lastTime = cooldowns.current.get(rawCode);
    if (lastTime && now - lastTime < 5000) return;

    inFlight.current = true;
    setError(null);

    const rowIndex =
      selectedRow === 'auto' ? findFirstAvailableRow() : Number(selectedRow);

    if (rowIndex === null) {
      setError('Все ряды заполнены');
      inFlight.current = false;
      return;
    }

    try {
      const { addedMark, currentCount } = await api.post(
        `/containers/${containerId}/scan-mark`,
        { code: rawCode, rowIndex }
      );

      beepAudio.current?.play().catch(() => {});
      setLastScan({ addedMark, currentCount });
      onScanSuccess?.(addedMark, currentCount);

      setInfo((prev) =>
        prev
          ? {
              ...prev,
              currentCount,
              marks: [...(prev.marks || prev.marksInContainer), addedMark],
            }
          : prev
      );

      cooldowns.current.set(rawCode, now);
    } catch (e) {
      setError(e.response?.data?.error || 'Ошибка при сканировании');
      cooldowns.current.set(rawCode, now);
    } finally {
      setTimeout(() => {
        inFlight.current = false;
      }, 500);
    }
  };

  const handleScannerTypeChange = (e) => {
    const value = e.target.value;
    setScannerType(value);
    localStorage.setItem('scannerType', value);
  };

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <button className={styles.closeBtn} onClick={onClose}>
          ×
        </button>
        <h2 className={styles.title}>
          {loadingInfo ? 'Загрузка...' : `Контейнер #${containerId}`}
        </h2>

        {!loadingInfo && info && (
          <>
            {/* BASIC INFO */}
            <div className={styles.info}>
              <div><strong>Код:</strong> {info.code}</div>
              <div><strong>Тип:</strong> {humanType}</div>
              <div><strong>Штук:</strong> {displayedCount}</div>
            </div>

            {/* LAYOUT ROWS */}
            {layoutRows.length > 0 && (
              <div className={styles.rowsInfo}>
                <strong>Ряды:</strong>
                <ul>
                  {layoutRows.map((cap, i) => {
                    const count = countsByRow[i];
                    const percent = Math.min(100, (count / cap) * 100);
                    const full = count >= cap; // полностью заполнен?

                    return (
                      <li
                        key={i}
                        className={full ? styles.fullRow : ''}
                      >
                        <div className={styles.rowLabel}>
                          {i + 1}: {count}/{cap}
                        </div>
                        <div className={styles.progressBar}>
                          <div
                            className={styles.progressFill}
                            style={{ width: `${percent}%` }}
                          ></div>
                        </div>
                      </li>
                    );
                  })}
                </ul>

              </div>
            )}
          </>
        )}

{/* CONTROLS */}
<div className={styles.controlsRow}>
  {layoutRows.length > 0 && (
    <div className={styles.controlGroup}>
      <label>Ряд:</label>
      <select
        value={selectedRow}
        onChange={(e) => setSelectedRow(e.target.value)}
      >
        <option value="auto">Авто</option>
        {layoutRows.map((_, i) => (
          <option key={i} value={i}>
            {i + 1}
          </option>
        ))}
      </select>
    </div>
  )}

  <div className={styles.controlGroup}>
    <label>Сканер:</label>
    <select value={scannerType} onChange={handleScannerTypeChange}>
      <option value="tsd">TSD</option>
      <option value="camera">Камера</option>
    </select>
  </div>
</div>


        {/* SCANNER */}
        <div className={styles.scannerWrapper}>
          {scannerType === 'tsd' ? (
            <TSDScanner onMarkScanned={handleMarkScanned} />
          ) : (
            <Scanner onMarkScanned={handleMarkScanned} />
          )}
        </div>

        {/* STATUS */}
        {error ? (
          <div className={styles.error}>{error}</div>
        ) : lastScan ? (
          <div className={styles.success}>
            Марка: <code>{lastScan.addedMark.mark_code}</code>
          </div>
        ) : null}
      </div>
    </div>
  );
}
